import { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { varAlpha, mergeClasses } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { useTranslate } from 'src/locales';

import { Image } from '../image';
import { Iconify } from '../iconify';
import { uploadClasses } from './classes';

import type { UploadProps } from './types';

// ----------------------------------------------------------------------

export function UploadAvatar({
  sx,
  error,
  value,
  disabled,
  helperText,
  className,
  uploadPhotoLabel = 'Upload photo',
  isSmall = false, // manage size
  setError,
  name,
  ...other
}: UploadProps) {
  const { t } = useTranslate('translation');

  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple: false,
    disabled,
    accept: { 'image/*': [] },
    onDropRejected: (rejections) => {
      if (setError && name) {
        const firstError = rejections[0]?.errors?.[0];
        const message = firstError?.message || 'File rejected';
        if (message === 'File is larger than 3145728 bytes') {
          setError(name, { type: 'manual', message: t('PLEASE_UPLOAD_A_FILE_BELOW_3_MB') });
        } else if (
          message === 'File type must be one of image/png, .png, image/jpeg, .jpg, .jpeg'
        ) {
          setError(name, { type: 'manual', message: t('ONLY_IMAGE_FILES_ARE_ALLOWED') });
        } else {
          setError(name, { type: 'manual', message });
        }
      }
    },

    ...other,
  });

  const hasFile = !!value;

  const hasError = !!error;
  // const hasError = !!error;

  const [preview, setPreview] = useState('');

  useEffect(() => {
    if (typeof value === 'string') {
      setPreview(value);
    } else if (value instanceof File) {
      setPreview(URL.createObjectURL(value));
    }
  }, [value]);

  const renderPreview = () =>
    hasFile && (
      <Image
        alt="Avatar"
        src={preview}
        sx={{ width: 1, height: 1, borderRadius: '50%', background: '#fff' }}
      />
    );

  const renderPlaceholder = () => (
    <Box
      className="upload-placeholder"
      sx={(theme) => ({
        top: 0,
        gap: 1,
        left: 0,
        width: 1,
        height: 1,
        zIndex: 9,
        display: 'flex',
        borderRadius: '50%',
        position: 'absolute',
        alignItems: 'center',
        color: 'text.disabled',
        flexDirection: 'column',
        justifyContent: 'center',
        bgcolor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
        transition: theme.transitions.create(['opacity'], {
          duration: theme.transitions.duration.shorter,
        }),
        '&:hover': { opacity: 0.72 },
        ...(hasError && {
          color: 'error.main',
          bgcolor: varAlpha(theme.vars.palette.error.mainChannel, 0.08),
        }),
        ...(hasFile && {
          zIndex: 9,
          opacity: 0,
          color: 'common.white',
          bgcolor: varAlpha(theme.vars.palette.grey['900Channel'], 0.64),
        }),
      })}
    >
      <Iconify icon="solar:camera-add-bold" width={isSmall ? 22 : 32} />

      {!isSmall && (
        <Typography variant="caption" fontSize={isSmall ? 9 : undefined}>
          {hasFile ? 'Update photo' : uploadPhotoLabel}
        </Typography>
      )}
    </Box>
  );

  const renderContent = () => (
    <Box
      sx={{
        width: 1,
        height: 1,
        overflow: 'hidden',
        borderRadius: '50%',
        position: 'relative',
      }}
    >
      {renderPreview()}
      {renderPlaceholder()}
    </Box>
  );

  return (
    <>
      <Box
        {...getRootProps()}
        className={mergeClasses([uploadClasses.uploadBox, className])}
        sx={[
          (theme) => ({
            p: 1,
            m: 'auto',
            width: isSmall ? 90 : 144, // Adjust size based on isSmall
            height: isSmall ? 90 : 144, // Adjust size based on isSmall
            cursor: 'pointer',
            overflow: 'hidden',
            borderRadius: '50%',
            border: `1px dashed ${varAlpha(theme.vars.palette.grey['500Channel'], 0.2)}`,
            ...(isDragActive && { opacity: 0.72 }),
            ...(disabled && { opacity: 0.48, pointerEvents: 'none' }),
            ...(hasError && { borderColor: 'error.main' }),
            ...(hasFile && {
              ...(hasError && { bgcolor: varAlpha(theme.vars.palette.error.mainChannel, 0.08) }),
              '&:hover .upload-placeholder': { opacity: 1 },
            }),
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <input {...getInputProps()} />

        {renderContent()}
      </Box>

      {helperText && helperText}

      {/* {!!fileRejections.length && <RejectionFiles files={fileRejections} />} */}
    </>
  );
}
